/**
 * Chat widget model
 */
import springyModel from '../tools/SpringyModel.js';
import springyRegex from '../tools/springyRegex.js';
import StatusResponse from './StatusResponse.js';

/**
 * Model for custom fields
 * @prop {String} name     Name/label for field.
 * @prop {Number} id       Which field it is (1,2,3)
 * @prop {Boolean} show     Is the field being used/shown.
 * @prop {Boolean} required Is the field required.
 * @prop {String} type     Input type: t=test, l=list
 * @prop {String[]} val      Values for list.
 */
class WidgetCustomField {
    /**
     * Constructor args match how data is stored
     * Not final property types.
     */
    constructor({
        name = '',
        id = 0,
        show = 0,
        required = 0,
        type = 't',
        val = '',
    }) {
        this.name = name;
        // Backwards compat
        // Unused fields seem to _all_ be set with this name
        // because of old chat widget builder
        if (name === 'click to edit') {
            this.name = '';
        }
        this.id = id;
        this.show = !!show;
        this.required = !!required;
        if (type !== 't' && type !== 'l') {
            type = 't';
        }
        this.type = type;
        this.val = Array.isArray(val) ? val : val.split(',').filter((el) => el.trim() !== '');
        // Don't show fields with no options.
        if (this.type === 'l' && this.val.length === 0) {
            this.show = false;
        }
        // Don't show field with no name
        if (this.name === '') {
            this.show = false;
        }
    }
}

const chatWidget = Object.create(springyModel);
/**
 * Defaults for widget (these get saved)
 * @prop {Number} id Primary key in database.
 * @prop {Number} iid LA Site iid.
 * @prop {String} hash Unique widget hash.
 * @prop {String} name Name of widget.
 * @prop {String} note Internal note for the widget.
 * @prop {String} key Hashed key for checking on start session.
 * @prop {Number} queue_id Queue to send tickets to.
 * @prop {Number} auth_id LibAuth config id.
 * @prop {String} flow_id Chatbot Flow uuid.
 * @prop {Number} unit_id Consortial site sub site unit id.
 * @prop {String} defaultProfile Url for default op profile svg
 * @prop {String} domain LA site domain including protocol.
 * @prop {String} dataNotice Data submission notification. Comes from LASite property.
 * @prop {String} greetingLocal Greeting after first op joins and they are local to widget site.
 * @prop {String} greetingCoop Greeting after first op joins and they are NOT local to widget site.
 * @prop {Number} timedFallback (Co-Op only) Seconds after which to fallback to Co-op when chat is unanswered.
 * @prop {Boolean} followup_enabled Will the patron be able to create a follow-up ticket from the chat.
 * @prop {String} authUrl LibAuth Login url if enabled.
 * @prop {String} authText LibAuth Login instructions.
 * @prop {String} authButton Libaith Login Button text.
 * @prop {Boolean} isAuthValidated Do we think the patron just validated through LibAuth
 * @prop {WidgetCustomField[]} customFields 0-3 Custom field objects. Replaces user1, user2, user3
 * @prop {StatusResponse|false} online_rule Current online op/dept/coop status
 * @prop {String} survey_uuid NPS Survey uuid to replace chat ratings.
 * @prop {String} language Widget language
*/
chatWidget.defaults = {
    id: 0,
    iid: 0,
    hash: '',
    name: '',
    note: '',
    language: '',
    key: '',
    queue_id: 0,
    auth_id: 0,
    flow_id: '',
    unit_id: 0,

    chat_title: 'Welcome to LibChat!',
    chat_intro: '',
    byeMsg: 'Thanks for chatting!',
    dept_label: 'Select Department:',
    name_label: 'Name (blank=anonymous)',
    name_default: '',
    guest_label: 'Guest',
    contact_label: 'Contact Info',
    dept_message: 'You are now chatting with %deptName% Department.',

    width: '100%',
    height: 340,

    chat_button: 'Start Chat',
    press_enter: 'Press ENTER to send',
    submit_button: 'Submit',
    email_trans: 'View/Email Transcript',

    offline_text: 'Ask Us', // header for offline pane
    offline_url: '', // for buttons

    slidebutton_url: '', // "//laimages.s3.amazonaws.com/data/libchat/chat_button_online.png", depending on region
    slidebutton_url_off: '', // "//laimages.s3.amazonaws.com/data/libchat/chat_button_offline.png", depending on region
    slidebutton_url_bot: '',
    slidebutton_text: 'Ask Us',
    slidebutton_text_off: 'Offline',
    slidebutton_text_bot: '',
    slidebutton_position: 'r', // r=right, l=left, t=top, b=bottom
    slidebutton_bcolor: '#286090',
    slidebutton_color: '#ffffff',
    slidebutton_bcolor_off: '#286090',
    slidebutton_color_off: '#ffffff',
    slidebutton_bcolor_bot: '#286090',
    slidebutton_color_bot: '#ffffff',
    slidebutton_width: '',
    slidebutton_height: '',

    la_hide: true,
    la_hide_msg: 'Sorry, chat is offline but you can still get help.',
    la_hide_msg2:
        '<a href="{{ domain }}" target="_parent">Search our Knowledgebase and/or submit your question</a>',
    la_search_opt: { group_id: 0, button: 'Search', placeholder: '', label: 'Search box' },
    la_search_box: '',

    star_text: 'How did we do?',
    rate_1: 'Bad',
    rate_2: 'So-so',
    rate_3: 'Good',
    rate_4: 'Great',
    survey_uuid: '',

    trans: 'Enter an email address to send this chat transcript to:',

    error_sess: 'Error starting session.',
    error_send: 'Error sending this message.',

    initial_question: false,
    initial_question_label: 'Your Question',

    comments_label: 'Any comments?',
    comments_button_text: 'Submit Feedback',
    comments_followup: 'I would like to be contacted for a follow-up.',
    comments_email: 'Email yourself a transcript of this chat',

    enable_anon: true,
    enable_sound: false,
    star_ratings: true,
    file_uploads: true,

    file_title: 'Upload File',
    file_intro: 'Note: Maximum file size is 20MB. File is removed after one month, it is not kept permanently.',
    file_label: 'Attach a file',
    file_action: 'Upload',
    max_file_size_label: 'Maximum File Size',
    clear_file_btn: 'Clear File',
    restart_chat: 'Start New Chat',

    cancel_button: 'Cancel',

    custom_css: '',

    color_backg: '#f9f9f9',
    color_head: '#286090',
    color_btn: '#FFFFFF',
    color_border: '',

    customFields: [],
    // Backwards compat until we refactor widget builder.
    // user1:                  null,
    // user2:                  null,
    // user3:                  null,

    error_off: 'Sorry it doesn\'t appear any librarians are online... Please try again later.',
    wait: 'Please wait... A librarian will connect shortly!',

    onlineRules: [{ d: [], u: 0, c: '' }], // In widget builder

    widget_type: 2,

    skip_login: false, // if true the login form is not shown
    use_captcha: false, // if true patron must pass a captcha before a chat session is started
    nologin_message: 'Type your question in the box below and press Enter to start chatting.', // if skip_login==true then this message is shown in chat area

    autoload_time: 0,
    autoload_head: 'Do you need help?',
    autoload_text: 'A librarian is online ready to help.',
    autoload_yes: 'Chat Now',
    autoload_no: 'No Thanks',
    autoload_default: true, // use the default dept/bot image (As opposed to custom image/name)
    autoload_image: '',
    autoload_name: '',

    missedchat_time: 30, // in seconds
    missedchat_message: 'We apologize for the delay. Don\'t want to wait?',
    missedchat_link: 'Submit your question.',

    error_link: 'Submit your question.',

    away_message: 'Chat is online but the operator is temporarily away.',
    away_link: 'Submit your question.',
    reload_button: 'Recheck Status',

    valid_form_q: 'Please enter your question.', // error when question is required and not filled out
    valid_form_n: 'Please enter your name.', // error when name is required and not filled out
    valid_form_c: 'Please enter valid contact information.', // error when contact is required or invalid

    fbwidget: false, // is it in the feedbackwidget
    autopop: false, // was it auto-popped-up (used in instatiation not as setting)

    question_text: '', // question text to autofill in login form (not saved with widget)

    contact_request: false, // are we asking for contact info, when making a new widget this defaults to true
    contact_required: false, // are we requiring contact info before chat (dependent on contact_request===true)
    contact_request_msg: 'To help us be able to follow up with you, please enter an email address or sms number.', // message used when asking for contact info.

    followup_enabled: true,
    // message user is offered the change to send a message to start a ticket.

    followup_msg: 'If you wish to be followed up with via email or phone, submit a message below to create a new support ticket.',
    cal_offline: false, // include option for libcal link/button offline
    cal_autoload: false, // include libchat link in autoload pane
    cal_text: 'Schedule a Meeting', // text of button
    cal_url: '', // url

    online_rule: false,
    translation: {},
    domain: '',
    defaultProfile: '',
    dataNotice: '',
    greetingLocal: '',
    greetingCoop: 'A partner librarian from our Cooperative is joining the chat.',
    timedFallback: 0,
    authUrl: '',
    authText: '',
    authButton: '',
    isAuthValidated: false, // Don't trust this, confirm server side
};
/**
 * @prop {Boolean} peel Was the widget was peeled out and we are now in the peeled version.
 */
chatWidget.peel = false;
/**
 * @prop {String} client_question What the client asked
 */
chatWidget.client_question = '';
/**
 * Widget type pseudo-constants
 */
chatWidget.widget_types = {
    button: 1,
    embed: 2,
    tab: 3,
    float: 6,
};
/**
 * @prop {String} referer Referer url.
 */
chatWidget.referer = '';
/**
 * @prop {String} referer_title Referer page name.
 */
chatWidget.referer_title = '';
/**
 * @prop {Boolean} loggedIn Was the login form submitted.
 */
chatWidget.loggedIn = false;
/**
 * @prop {Object} cascade A socket.io object connected to chat server.
 */
chatWidget.cascade = null;
/**
 * @prop {Boolean} connected Is patron connected to chat.
 */
chatWidget.connected = false;
/**
 * @prop {Boolean} reconnect Should we try to reconnect?
 */
chatWidget.reconnect = true;
/**
 * This helps us known the difference between a connect and a reconnect.
 * @prop {Boolean} hasConnected Did we ever connect?
 */
chatWidget.hasConnected = false;
/**
 * This helps us known when the patron has actually connected and registered as a user in chat.
 * Before this point we want to treat disconnects/errors/etc differently.
 * @prop {Boolean} hasSubscribed Did they successfully subscribe to chat.
 */
chatWidget.hasSubscribed = false;
/**
 * @prop {Boolean} chatting Is there currently an op on the other side.
 */
chatWidget.chatting = false;
/**
 * @prop {Boolean} ticketCreated Have we created a ticket this session.
 */
chatWidget.ticketCreated = false;
/**
 * @prop {Boolean} answered Was this chat ever answered.
 */
chatWidget.answered = false;
/**
 * @prop {Boolean} peelingParent Was this widget peeled out, if so this is the disconnected shell
 */
chatWidget.peelingParent = false;
/**
 * pseudo constants from cascade to identify op/dept status
 */
chatWidget.status_offline = 0;
chatWidget.status_online = 1;
chatWidget.status_away = 2;
/**
 * Start up the widget with some properties
 * @param {Object} config Property/values to set.
 * @param {Object} emitter eventEmitter object.
 */
chatWidget.initialize = function (config, emitter) {
    this.convertCustomFields(config);
    // widget_type should be an integer
    if (config.widget_type) {
        config.widget_type = parseInt(config.widget_type, 10);
    }
    // make sure these are integers.
    // as sometimes they end up as strings from the big settings object
    if (config.iid) {
        config.iid = Number(config.iid);
    }
    if (config.queue_id) {
        config.queue_id = Number(config.queue_id);
    }
    if (config.timedFallback) {
        config.timedFallback = Number(config.timedFallback);
    }

    // set the properties;
    this.init(config, emitter);
};

/**
 * Convert customfields from the user1,2,3 we store to the CustomField class/array.
 * @param {Object} data Widget props.
 */
chatWidget.convertCustomFields = function (data) {
    // Handles when the new class array is already set
    // In the builder only for now.
    if (data.customFields && Array.isArray(data.customFields)) {
        data.customFields.forEach((el, i) => {
            if (el instanceof WidgetCustomField) {
                return;
            }
            if (!el.id) {
                return;
            }
            data.customFields[i] = new WidgetCustomField(el);
        });
        return;
    }
    // add the custom field defs
    const customFields = [];
    for (let i = 1; i <= 3; i++) {
        // Stored in db json as user1/2/3 properties.
        const vname = `user${i}`;
        // Always set the field to the class.
        // the show prop will handle whether we should see it.
        const cfield = data[vname] || {};
        cfield.id = i;
        const customField = new WidgetCustomField(cfield);
        customFields.push(customField);
        delete data[vname];
    }
    data.customFields = customFields;
};
/**
 * Get a url for a Question Form
 * @return {String}
 */
chatWidget.getFormUrl = function () {
    return `${this.get('domain')}/form?queue_id=${this.get('queue_id')}&pquestion=${encodeURIComponent(this.get('client_question'))}`;
};
/**
 * Link to the Site question form page.
 * @param {String} linkText Text for link
 * @returns {String}
 */
chatWidget.getFormLink = function (linkText) {
    return `<a href="${this.getFormUrl()}" target="_blank">${linkText}</a>`;
};
/**
 * create the missed chat message for display
 * @return {String}
 */
chatWidget.getMissedChatMessage = function () {
    let o = '';
    const message = this.get('missedchat_message');
    if (message !== '') {
        o += message;
    }
    if (!this.get('followup_enabled')) {
        return o;
    }
    const link = this.get('missedchat_link');
    if (link !== '') {
        o += `<br/><br/>${this.getFormLink(link)}`;
    }
    return o;
};
/**
 * create a message for display when a connection error occurs
 * @return {String}
 */
chatWidget.getErrorMessage = function () {
    let message = this.get('translation').ch_connect_error;

    // If followup is enabled stop here.
    if (this.get('followup_enabled')) {
        return message;
    }
    // Add a link to the question form.
    const link = this.get('error_link');
    if (link !== '') {
        message += `<br/><br/>${this.getFormLink(link)}`;
    }
    return message;
};
/**
 * create a message for display when a connection is successful but for some reason no one is online
 * @return {String}
 */
chatWidget.getOfflineMessage = function () {
    let o = '';
    const message = this.get('error_off');
    if (message !== '') {
        o += message;
    }
    if (!this.get('followup_enabled')) {
        return o;
    }
    if (this.get('contact_request')) {
        return o;
    }
    const link = this.get('error_link');
    if (link !== '') {
        o += `<br/><br/>${this.getFormLink(link)}`;
    }
    return o;
};
/**
 * create a message for display when op is away
 * @return {String}
 */
chatWidget.getAwayMessage = function () {
    let o = '';
    const message = this.get('away_message');
    if (message !== '') {
        o += message;
    }
    if (!this.get('followup_enabled')) {
        return o;
    }
    if (this.get('contact_request')) {
        return o;
    }
    const link = this.get('away_link');
    if (link !== '') {
        o += `<br/><br/>${this.getFormLink(link)}`;
    }
    return o;
};
/**
 * Is this a button type widget
 * @return {Boolean}
 */
chatWidget.isButton = function () {
    const wtype = parseInt(this.get('widget_type'), 10);
    if (wtype === this.get('widget_types').button) {
        return true;
    }
    return false;
};
/**
 * Is this a float widget
 * @return {Boolean}
 */
chatWidget.isFloat = function () {
    const wtype = parseInt(this.get('widget_type'), 10);
    if (wtype === this.get('widget_types').float) {
        return true;
    }
    return false;
};
/**
 * Is this a tab widget
 * @return {Boolean}
 */
chatWidget.isTab = function () {
    const wtype = parseInt(this.get('widget_type'), 10);
    if (wtype === this.get('widget_types').tab) {
        return true;
    }
    return false;
};
/**
 * Is this an embed widget
 * @return {Boolean}
 */
chatWidget.isEmbed = function () {
    const wtype = parseInt(this.get('widget_type'), 10);
    if (wtype === this.get('widget_types').embed) {
        return true;
    }
    return false;
};
/**
 * offline link target based on type (or peel status)
 * @return {String}
 */
chatWidget.offlineLinkTarget = function () {
    if (this.get('peel')) {
        return '_blank';
    }
    return this.isButton() ? '_blank' : '_parent';
};
/**
 * Generate a random name for anon users
 * @return {String}
 */
chatWidget.generateAnonName = function () {
    const guestnum = Math.floor(Math.random() * 99999999);
    return this.get('guest_label') + guestnum;
};
/**
 * Validate data for client_contact field.
 * @param {String} text The entered text.
 * @return {Boolean}
 */
chatWidget.validateContact = function (text) {
    if (this.get('contact_required') && text === '') {
        return false;
    }
    if (text === '') {
        return true;
    }
    if (text.match(springyRegex.email) === null) {
        const temp_contact = text.replace(/[-()\s.]/g, '');
        if (temp_contact.match(springyRegex.phone) === null) {
            return false;
        }
    }
    return true;
};
/**
 * Get a url to be used for peeling out this widget
 * @param {String} peelKey Key for checking info server side
 * @return {String}
 */
chatWidget.getPeelUrl = function (peelKey) {
    // Relative will open on the same domain as the iframe.
    return `/chat/widget/${this.get('hash')}?peelKey=${peelKey}&referer=${this.get('referer')}`;
};
/**
 * Should we bring focus to the widget on load?
 * @return {Boolean}
 */
chatWidget.focusOnLoad = function () {
    return !(this.get('widget_type') === this.get('widget_types').embed);
};
/**
 * Convert the user submitted text
 * Encode < for html safety and convert urls to links
 * @param {String} msg Message string
 * @return {String}
 */
chatWidget.sanitizeMessage = function (msg) {
    return msg
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#x27;')
        .replace(
            /(^|\s|&nbsp;)(https?:[^\s"\n]+?)([.,!]?(\s|\n|$|<|&nbsp;))/g,
            '$1<a href="$2" target="_blank">$2</a>$3',
        );
};
/**
 * Get the greeting message to show when first op joins.
 * @param {Boolean} isLocalOp Is the op from the same site as the widget.
 * @return {String}
 */
chatWidget.getGreeting = function (isLocalOp) {
    if (isLocalOp) {
        return this.get('greetingLocal');
    }
    return this.get('greetingCoop');
};

/**
 * Is LibAuth validated or not set up
 * @returns {Boolean}
 */
chatWidget.isAuthValidOrNotSet = function () {
    if (this.get('auth_id') <= 0 || this.get('isAuthValidated')) {
        return true;
    }
    return false;
};

chatWidget.isOnline = function () {
    const rules = this.get('online_rule');
    if (rules === false || !(rules instanceof StatusResponse)) {
        return false;
    }
    return rules.isOnline;
};

export {
    WidgetCustomField,
    chatWidget,
};
